import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Alert, Badge, Button, Card, CardBody, Checkbox, Field, Input, PageHeader, Select, Textarea } from "@fellowflats/ui";
import { OkSchema } from "@fellowflats/contracts/account-writes";
import { AdminListingSchema, type AdminListing } from "@fellowflats/contracts/admin";
import { CreatedReplySchema, PlacedReplySchema, type AmenityBody, type RoomBody } from "@fellowflats/contracts/listing-writes";
import { GenderRuleSchema, PropertyTypeSchema, RoomTypeSchema } from "@fellowflats/contracts/listings";
import { api } from "../lib/api";
import { config } from "../lib/config";
import { describe, useApi } from "../lib/use-api";
import { ActionButton, Loaded, day } from "../components/bits";
import { statusTone, words } from "./listings";

/** Typed dollars to integer cents (money on the wire is always cents). NaN when not a number. */
const toCents = (v: string) => Math.round(Number.parseFloat(v.replace(/[$,\s]/g, "")) * 100);
const toDollars = (cents: number | null | undefined) => (cents == null ? "" : String(cents / 100));

/**
 * One listing, whoever owns it. Every change runs the owner's own rules in the
 * API (lawful charges only, beds never below the filled count, publishing only
 * with a verified owner), so staff cannot save a listing a landlord could not.
 */
export function ListingEditor() {
  const { id = "" } = useParams();
  const state = useApi(() => api("GET", `/admin/listings/${id}`, AdminListingSchema), id);
  return (
    <div className="space-y-6">
      <Link to="/listings" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} aria-hidden="true" /> All listings
      </Link>
      <Loaded state={state}>{(l) => <Editor key={l.id} listing={l} reload={state.reload} />}</Loaded>
    </div>
  );
}

function Editor({ listing: l, reload }: { listing: AdminListing; reload: () => void }) {
  const navigate = useNavigate();
  const setStatus = (status: "DRAFT" | "ACTIVE" | "PAUSED" | "ARCHIVED") => api("PUT", `/admin/listings/${l.id}/status`, OkSchema, { status }).then(reload);
  return (
    <>
      <PageHeader
        title={l.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone[l.status]}>{words(l.status)}</Badge>
            {l.isFeatured && <Badge tone="primary">featured until {day(l.featuredUntil)}</Badge>}
            {l.verificationStatus === "APPROVED" && <Badge tone="success">verified listing</Badge>}
            <span>
              {l.city}, {l.province} · {l.listingType === "ENTIRE_UNIT" ? "whole unit" : "by the bed"} · created {day(l.createdAt)}
            </span>
          </span>
        }
        actions={
          <a
            href={`${config.siteUrl}/listings/${l.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            View on site <ExternalLink size={14} aria-hidden="true" />
          </a>
        }
      />

      <Section title="Owner">
        <p className="text-sm">
          <span className="font-medium">{l.owner.fullName ?? "-"}</span> <span className="text-muted-foreground">{l.owner.email}</span>{" "}
          <Badge tone={l.owner.verifiedLandlord ? "success" : "warning"}>{l.owner.verifiedLandlord ? "verified landlord" : "not verified"}</Badge>{" "}
          {l.owner.status === "SUSPENDED" && <Badge tone="danger">suspended</Badge>}
        </p>
        {!l.owner.verifiedLandlord && (
          <p className="text-sm text-muted-foreground">
            Publishing needs the owner approved first, in{" "}
            <Link to="/verifications" className="text-primary hover:underline">
              Verifications
            </Link>
            .
          </p>
        )}
      </Section>

      <Section title="Status" hint="The owner is notified when the team pauses, archives, drafts or deletes their listing.">
        <div className="flex flex-wrap gap-2">
          {l.status !== "ACTIVE" && (
            <ActionButton variant="primary" run={() => setStatus("ACTIVE")}>
              Publish
            </ActionButton>
          )}
          {l.status === "ACTIVE" && <ActionButton run={() => setStatus("PAUSED")}>Pause (unpublish)</ActionButton>}
          {l.status !== "DRAFT" && <ActionButton run={() => setStatus("DRAFT")}>Move to draft</ActionButton>}
          {l.status !== "ARCHIVED" && (
            <ActionButton variant="ghost" confirm={`Take "${l.title}" down? It leaves search and its page.`} run={() => setStatus("ARCHIVED")}>
              Archive (take down)
            </ActionButton>
          )}
          {l.isFeatured ? (
            <ActionButton variant="ghost" run={() => api("PUT", `/admin/listings/${l.id}/featured`, OkSchema, { featured: false }).then(reload)}>
              Stop featuring
            </ActionButton>
          ) : (
            <ActionButton variant="ghost" run={() => api("PUT", `/admin/listings/${l.id}/featured`, OkSchema, { featured: true, days: 30 }).then(reload)}>
              Feature for 30 days
            </ActionButton>
          )}
        </div>
      </Section>

      <Details listing={l} reload={reload} />
      <Rooms listing={l} reload={reload} />
      <Amenities listing={l} reload={reload} />
      <Photos listing={l} reload={reload} />

      <Section
        title="Delete permanently"
        hint="Removes the listing, its rooms, photos and videos for good. Not possible once it has had a booking: archive it instead, so the history is kept."
      >
        {l.bookings > 0 ? (
          <p className="text-sm text-muted-foreground">This listing has {l.bookings} booking(s), so it can only be archived.</p>
        ) : (
          <ActionButton
            variant="danger"
            confirm={`Delete "${l.title}" permanently? This cannot be undone.`}
            run={() => api("DELETE", `/admin/listings/${l.id}`, OkSchema).then(() => navigate("/listings"))}
          >
            Delete listing
          </ActionButton>
        )}
      </Section>
    </>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Card>
      <CardBody className="space-y-3 p-5">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
        </div>
        {children}
      </CardBody>
    </Card>
  );
}

/** A form's save button, with the API's refusal shown under it. */
function useSave() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await fn();
      setSaved(true);
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };
  const footer = (label = "Save") => (
    <div className="flex items-center gap-3">
      <Button type="submit" size="sm" loading={busy}>
        {label}
      </Button>
      {saved && <span className="text-sm text-success">Saved</span>}
      {error && <span className="text-sm text-danger">{error}</span>}
    </div>
  );
  return { run, footer };
}

/* ── Details and location ── */

function Details({ listing: l, reload }: { listing: AdminListing; reload: () => void }) {
  const [f, setF] = useState({
    title: l.title,
    description: l.description ?? "",
    propertyType: l.propertyType,
    addressLine1: l.addressLine1 ?? "",
    city: l.city,
    province: l.province,
    postalCode: l.postalCode ?? "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useSave();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => {
      await api("PUT", `/admin/listings/${l.id}`, PlacedReplySchema, {
        ...f,
        country: l.country,
        // The picked city stays linked while its name is unchanged; a retyped city is matched again.
        ...(l.cityId && f.city.trim() === l.city ? { cityId: l.cityId } : {}),
      });
      reload();
    });
  };
  return (
    <Section title="Details and location" hint="The web address (slug) never changes. A changed address is placed on the map again.">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Title" htmlFor="title" required>
          <Input id="title" value={f.title} onChange={set("title")} minLength={3} maxLength={120} required />
        </Field>
        <Field label="Description" htmlFor="description">
          <Textarea id="description" value={f.description} onChange={set("description")} maxLength={4000} rows={6} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Property type" htmlFor="propertyType">
            <Select id="propertyType" value={f.propertyType} onChange={set("propertyType")}>
              {PropertyTypeSchema.options.map((t) => (
                <option key={t} value={t}>
                  {words(t)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Street address" htmlFor="addressLine1" hint="Never shown publicly.">
            <Input id="addressLine1" value={f.addressLine1} onChange={set("addressLine1")} maxLength={160} />
          </Field>
          <Field label="City" htmlFor="city" required>
            <Input id="city" value={f.city} onChange={set("city")} maxLength={80} required />
          </Field>
          <Field label="Province" htmlFor="province">
            <Input id="province" value={f.province} onChange={set("province")} maxLength={40} />
          </Field>
          <Field label="Postal code" htmlFor="postalCode" hint="Never shown publicly.">
            <Input id="postalCode" value={f.postalCode} onChange={set("postalCode")} maxLength={12} />
          </Field>
        </div>
        {save.footer("Save details")}
      </form>
    </Section>
  );
}

/* ── Rooms and prices ── */

type RoomRow = AdminListing["rooms"][number];

function Rooms({ listing: l, reload }: { listing: AdminListing; reload: () => void }) {
  const whole = l.listingType === "ENTIRE_UNIT";
  return (
    <Section
      title={whole ? "Rent" : "Rooms and prices"}
      hint={
        whole
          ? "A whole unit is one room of one bed whose price is the rent. The site shows the all-in price: this rent plus any mandatory monthly charges."
          : "Prices are per bed per month, before mandatory monthly charges (the site adds those to the headline price). Capacity never drops below the beds already filled."
      }
    >
      <div className="space-y-3">
        {l.rooms.map((r) => (
          <RoomForm key={r.id} listingId={l.id} room={r} whole={whole} reload={reload} />
        ))}
        {!whole && <RoomForm listingId={l.id} whole={false} reload={reload} />}
      </div>
    </Section>
  );
}

function RoomForm({ listingId, room, whole, reload }: { listingId: string; room?: RoomRow; whole: boolean; reload: () => void }) {
  const blank = { label: "", capacity: "1", price: "", roomType: "PRIVATE", genderRule: "ANY", furnished: false };
  const [f, setF] = useState(
    room
      ? {
          label: room.label,
          capacity: String(room.capacity),
          price: toDollars(room.pricePerSlot),
          roomType: room.roomType,
          genderRule: room.genderRule,
          furnished: room.furnished,
        }
      : blank,
  );
  const save = useSave();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => {
      const body: RoomBody = {
        label: f.label.trim(),
        capacity: Number.parseInt(f.capacity, 10),
        priceCents: toCents(f.price),
        roomType: f.roomType as RoomBody["roomType"],
        genderRule: f.genderRule as RoomBody["genderRule"],
        furnished: f.furnished,
      };
      if (!Number.isInteger(body.priceCents) || body.priceCents <= 0) throw new Error("Enter a price in dollars, more than 0.");
      if (room) await api("PUT", `/admin/listings/${listingId}/rooms/${room.id}`, PlacedReplySchema, body);
      else {
        await api("POST", `/admin/listings/${listingId}/rooms`, CreatedReplySchema, body);
        setF(blank);
      }
      reload();
    });
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-md border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{room ? room.label : "Add a room"}</p>
        {room && (
          <span className="text-xs text-muted-foreground">
            {room.available} free · {room.occupied} filled · {room.activeBookings} open request(s)
          </span>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Label" htmlFor={`label-${room?.id ?? "new"}`} required>
          <Input id={`label-${room?.id ?? "new"}`} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} maxLength={80} required />
        </Field>
        <Field label={whole ? "Rent per month ($)" : "Price per bed per month ($)"} htmlFor={`price-${room?.id ?? "new"}`} required>
          <Input id={`price-${room?.id ?? "new"}`} inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} required />
        </Field>
        {!whole && (
          <Field label="Beds" htmlFor={`capacity-${room?.id ?? "new"}`} required>
            <Input
              id={`capacity-${room?.id ?? "new"}`}
              type="number"
              min={1}
              max={12}
              value={f.capacity}
              onChange={(e) => setF({ ...f, capacity: e.target.value })}
              required
            />
          </Field>
        )}
        {!whole && (
          <Field label="Room type" htmlFor={`type-${room?.id ?? "new"}`}>
            <Select id={`type-${room?.id ?? "new"}`} value={f.roomType} onChange={(e) => setF({ ...f, roomType: e.target.value })}>
              {RoomTypeSchema.options.map((t) => (
                <option key={t} value={t}>
                  {words(t)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {!whole && (
          <Field label="Who may book" htmlFor={`gender-${room?.id ?? "new"}`}>
            <Select id={`gender-${room?.id ?? "new"}`} value={f.genderRule} onChange={(e) => setF({ ...f, genderRule: e.target.value })}>
              {GenderRuleSchema.options.map((t) => (
                <option key={t} value={t}>
                  {words(t)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="flex items-end pb-2">
          <Checkbox label="Furnished" checked={f.furnished} onChange={(e) => setF({ ...f, furnished: e.target.checked })} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {save.footer(room ? "Save room" : "Add room")}
        {room && !whole && (
          <ActionButton
            variant="ghost"
            confirm={`Delete the room "${room.label}"?`}
            run={() => api("DELETE", `/admin/listings/${listingId}/rooms/${room.id}`, PlacedReplySchema).then(reload)}
          >
            Delete room
          </ActionButton>
        )}
      </div>
    </form>
  );
}

/* ── Amenities and charges ── */

type AmenityRow = { name: string; isPaid: boolean; price: string; billing: AmenityBody["billing"]; isOptional: boolean };

function Amenities({ listing: l, reload }: { listing: AdminListing; reload: () => void }) {
  const [rows, setRows] = useState<AmenityRow[]>(
    l.amenities.map((a) => ({ name: a.name, isPaid: a.isPaid, price: toDollars(a.priceCents), billing: a.billing, isOptional: a.isOptional })),
  );
  const save = useSave();
  const change = (i: number, patch: Partial<AmenityRow>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => {
      const amenities: AmenityBody[] = rows
        .filter((r) => r.name.trim())
        .map((r) => ({ name: r.name.trim(), isPaid: r.isPaid, priceCents: r.isPaid ? toCents(r.price) : null, billing: r.billing, isOptional: r.isOptional }));
      const unpriced = amenities.find((a) => a.isPaid && !(Number.isInteger(a.priceCents) && (a.priceCents ?? 0) > 0));
      if (unpriced) throw new Error(`Give "${unpriced.name}" a price in dollars, or untick Paid.`);
      await api("PUT", `/admin/listings/${l.id}/amenities`, PlacedReplySchema, { amenities });
      reload();
    });
  };
  return (
    <Section
      title="Amenities and charges"
      hint="A paid amenity that is not optional and billed monthly is mandatory, so it is added to the advertised price. Security, damage and pet deposits are refused (Ontario RTA)."
    >
      <form onSubmit={submit} className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No amenities yet.</p>}
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-end gap-3 rounded-md border border-border p-3">
            <div className="min-w-48 flex-1">
              <Field label="Name" htmlFor={`am-name-${i}`}>
                <Input id={`am-name-${i}`} value={r.name} onChange={(e) => change(i, { name: e.target.value })} maxLength={60} />
              </Field>
            </div>
            <div className="pb-2">
              <Checkbox label="Paid" checked={r.isPaid} onChange={(e) => change(i, { isPaid: e.target.checked })} />
            </div>
            {r.isPaid && (
              <>
                <div className="w-32">
                  <Field label="Price ($)" htmlFor={`am-price-${i}`}>
                    <Input id={`am-price-${i}`} inputMode="decimal" value={r.price} onChange={(e) => change(i, { price: e.target.value })} />
                  </Field>
                </div>
                <div className="w-36">
                  <Field label="Billing" htmlFor={`am-billing-${i}`}>
                    <Select id={`am-billing-${i}`} value={r.billing} onChange={(e) => change(i, { billing: e.target.value as AmenityRow["billing"] })}>
                      <option value="MONTHLY">monthly</option>
                      <option value="ONE_TIME">one time</option>
                    </Select>
                  </Field>
                </div>
                <div className="pb-2">
                  <Checkbox label="Optional" checked={r.isOptional} onChange={(e) => change(i, { isOptional: e.target.checked })} />
                </div>
              </>
            )}
            <Button type="button" size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setRows([...rows, { name: "", isPaid: false, price: "", billing: "MONTHLY", isOptional: true }])}
          >
            Add amenity
          </Button>
          {save.footer("Save amenities")}
        </div>
      </form>
    </Section>
  );
}

/* ── Photos ── */

function Photos({ listing: l, reload }: { listing: AdminListing; reload: () => void }) {
  return (
    <Section title="Photos" hint="Staff can remove a photo or choose the cover. New photos are added by the owner.">
      {l.images.length === 0 ? (
        <Alert>This listing has no photos.</Alert>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {l.images.map((img) => (
            <figure key={img.id} className="space-y-2">
              <img
                src={new URL(img.url, config.siteUrl).href}
                alt=""
                className="aspect-[4/3] w-full rounded-md border border-border object-cover"
                loading="lazy"
              />
              <figcaption className="flex flex-wrap items-center gap-1">
                {img.isCover ? (
                  <Badge tone="primary">cover</Badge>
                ) : (
                  <ActionButton variant="ghost" run={() => api("PUT", `/admin/listings/${l.id}/images/${img.id}/cover`, PlacedReplySchema).then(reload)}>
                    Make cover
                  </ActionButton>
                )}
                <ActionButton
                  variant="ghost"
                  confirm="Remove this photo for good?"
                  run={() => api("DELETE", `/admin/listings/${l.id}/images/${img.id}`, PlacedReplySchema).then(reload)}
                >
                  Remove
                </ActionButton>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {l.videos.length > 0 && <p className="text-sm text-muted-foreground">{l.videos.length} video(s), managed by the owner.</p>}
    </Section>
  );
}
